import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { DataService } from 'src/services/data-service';

interface SecurityDepositDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  leaseId: string;
  onSkip?: () => void;
}

const SecurityDepositDialog: React.FC<SecurityDepositDialogProps> = ({
  open,
  onClose,
  onSuccess,
  leaseId,
  onSkip
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().slice(0, 16)
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (formData.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (!formData.paymentDate) {
      setError('Please select a payment date');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        leaseId: leaseId,
        amount: Number(formData.amount),
        paymentDate: new Date(formData.paymentDate).toISOString()
      };

      console.log('Submitting security deposit:', payload);

      const response = await DataService.post('api/SecurityDeposit/create', payload);

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        let errorMessage = `Failed to create security deposit (${response.status})`;
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
        console.error('Security deposit creation failed:', errorMessage, 'Response:', response);
      }
    } catch (err) {
      console.error('Error creating security deposit:', err);
      setError('An error occurred while creating the security deposit');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      amount: 0,
      paymentDate: new Date().toISOString().slice(0, 16)
    });
    setError(null);
    onClose();
  };

  const handleSkip = () => {
    handleClose();
    if (onSkip) {
      onSkip();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceWalletIcon />
            <Typography variant="h6" fontWeight="bold">
              Add Security Deposit
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Lease created successfully! Would you like to add a security deposit for this lease?
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Deposit Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              required
              helperText="Enter the security deposit amount"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment Date & Time"
              type="datetime-local"
              value={formData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
              helperText="When was the deposit paid?"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleSkip} disabled={loading} variant="outlined">
          Skip for Now
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || formData.amount <= 0}
          startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceWalletIcon />}
        >
          {loading ? 'Adding...' : 'Add Security Deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecurityDepositDialog;
